import {ItemModelType, WorkspaceModelType} from "@stores/WorkspaceStore";
import intl from "react-intl-universal";

import {ItemType} from "../../API";
import {_apply_affected_items} from "./_apply_affected_items";
import {updateDocumentTrashed} from "../../sync/DocumentApiRequests";
import {
    updateWorkspaceChildIdsMoveToTrash, updateWorkspaceTrashedIdsMoveToTrashFromArchive,
    updateWorkspaceChildIdsRestoreFromTrash,
    updateWorkspaceTrashedIdsAdd
} from "../workspace/updateWorkspace";
import {updateItemChildIdsRemove, updateItemDB} from "./updateItem";
import {makeReverseClass, UndoableAction} from "../common/UndoableAction";
import {undoableFunctionCall} from "../common/undoableFunctionCall";
import {selectItemPlain} from "../ui/selectItem";
import {UndoableActionGroup} from "../common/UndoableActionGroup";
import {inAppEventsHub} from "../../volatile/InAppEventsHub";
import {logErrorToSentry} from "../../utils/errors/logErrorToSentry";
import {Item} from "../../@types/Item";
import {CrudServices} from "../../platform/services/types/ServiceDependencies";
import {DataSourceManager} from "@stores/dataSources/DataSourceManager";
import {UserSessionState} from "@stores/UserSessionState";
import type {AppState} from "@stores/AppState";
import {WsServerConnection} from "@stores/serverConnection/wsServerConnection";


export class UndoableMoveToTrash implements UndoableAction {
    private services: CrudServices;
    private appState: AppState;
    private itemId: string;
    private workspaceMST: WorkspaceModelType;

    constructor(
        services: CrudServices,
        appState: AppState,
        itemId: string,
        workspaceMST: WorkspaceModelType
    ) {
        this.services = services;
        this.appState = appState;
        this.itemId = itemId;
        this.workspaceMST = workspaceMST;
    }

    execute(): Promise<any> {
        return moveToTrash(this.services, this.appState, this.itemId, this.workspaceMST);
    }

    undo(): Promise<any> {
        return restoreFromTrash(this.services, this.appState, this.itemId, this.workspaceMST);
    }
}

export const UndoableRestoreFromTrash = makeReverseClass(UndoableMoveToTrash);

export const moveToTrashUndoable = (
    services: CrudServices,
    appState: AppState,
    itemId: string,
    workspace: WorkspaceModelType
) => {
    const undoableAction = new UndoableMoveToTrash(services, appState, itemId, workspace);
    return undoableFunctionCall(undoableAction, intl.get('snackbar.deleted').d('Page deleted.'));
}

export const restoreFromTrashUndoable = (
    services: CrudServices,
    appState: AppState,
    itemId: string,
    workspace: WorkspaceModelType
) => {
    const undoableAction = new UndoableRestoreFromTrash(services, appState, itemId, workspace);
    return undoableFunctionCall(undoableAction, intl.get('snackbar.restore_from_trash').d('Page restored.'))
}

export function moveToTrash(
    services: CrudServices,
    appState: AppState,
    itemId: string,
    workspace: WorkspaceModelType,
    level = 0,
    undoableActionGroup?: UndoableActionGroup
) {
    const userState = appState.userState!;
    const dsManager = userState.dsManager!;
    let onFinish: (() => void) | undefined;
    inAppEventsHub.dispatch({
        type: 'ItemDeleteStart',
        itemId: itemId,
    });
    onFinish = () => {
        inAppEventsHub.dispatch({
            type: 'ItemDeleteEnd',
            itemId: itemId,
        });
    }
    if (undoableActionGroup !== undefined) {
        const undoableAction = new UndoableMoveToTrash(services, appState, itemId, workspace);
        if (onFinish) {
            undoableActionGroup.after(onFinish);
        }
        return undoableActionGroup.add(undoableAction);
    } else {
        let finishPromise = _moveToTrash(services, appState, itemId, workspace, level);
        if (onFinish) {
            finishPromise = finishPromise.then(onFinish);
        }
        return finishPromise;
    }
}

function _moveToTrash(
    services: CrudServices,
    appState: AppState,
    itemId: string,
    workspace: WorkspaceModelType,
    level = 0
): Promise<any> {
    const userState = appState.userState!;
    const dsManager = userState.dsManager!;
    const wsUpdateServices = {...services, dsManager};
    let wsDbChange: Promise<any> = Promise.resolve();
    if (workspace.selectedId === itemId) {
        selectItemPlain(appState.rootStore, workspace, null);
    }

    const itemMST = workspace.items.get(itemId)!;

    itemMST.setTrashed(true);
    if (itemMST.archived) {
        itemMST.setArchived(false);
    }

    const waitAffected = _apply_affected_items(
        workspace,
        itemMST,
        async (item) => {
            // @ts-ignore
            await moveToTrash(services, appState, item.id, workspace, level + 1);
        }
    ).catch(logErrorToSentry);

    const waitDbChange = setItemTrashDB(services, dsManager, userState, workspace.id, itemMST, true).catch(logErrorToSentry);

    if (level === 0) {
        if (workspace.childIds.includes(itemId)) {
            wsDbChange = updateWorkspaceChildIdsMoveToTrash(wsUpdateServices, workspace, itemId);
        } else if (workspace.archivedIds.includes(itemId)) {
            wsDbChange = updateWorkspaceTrashedIdsMoveToTrashFromArchive(wsUpdateServices, workspace, itemId);
        } else {
            wsDbChange = updateWorkspaceTrashedIdsAdd(wsUpdateServices, workspace, itemId);
        }
        for (const parentId of itemMST.parentIds) {
            wsDbChange = wsDbChange.then(() => updateItemChildIdsRemove(services, dsManager, parentId, itemId, workspace));
        }
    }

    return Promise.all([waitDbChange, waitAffected, wsDbChange]);
}

export function restoreFromTrash(
    services: CrudServices,
    appState: AppState,
    itemId: string,
    workspace: WorkspaceModelType,
    level = 0
) {
    let wsDbChange: Promise<any> = Promise.resolve();
    const userState = appState.userState!;
    const dsManager = userState.dsManager!;
    const wsUpdateServices = {...services, dsManager};
    const itemMST = workspace.items.get(itemId)!;
    itemMST.setTrashed(false);
    if (itemMST.archived) {
        itemMST.setArchived(false);
    }

    const waitAffected = _apply_affected_items(workspace, itemMST, async (item) => {
        // @ts-ignore
        await restoreFromTrash(services, dsManager, userState, item.id, workspace, level + 1);
    }).catch(logErrorToSentry);

    const waitDbChange = setItemTrashDB(services, dsManager, userState, workspace.id, itemMST, false).catch(logErrorToSentry);

    if (level === 0) {
        if (!workspace.childIds.includes(itemId)) {
            wsDbChange = updateWorkspaceChildIdsRestoreFromTrash(wsUpdateServices, workspace, itemId);
        }
    }

    return Promise.all([waitDbChange, waitAffected, wsDbChange]);
}

async function setItemTrashDB(
    services: CrudServices,
    dsManager: DataSourceManager,
    userState: UserSessionState,
    workspaceId: string,
    itemMST: ItemModelType,
    value: boolean
) {
    if (itemMST.type === ItemType.LEAF) {
        try {
            await setDocumentTrash(userState.wsServerConnection, itemMST, value);
        } catch (error: any) {
            if (error.s === 404 || error.message === 'Not found') {
                // ignore error if document already deleted
            } else {
                throw error;
            }
        }
    }

    const fieldsToUpdate: Partial<Item> = {
        trashed: value,
        statusUpdatedBy: userState.currentUserId
    }
    if (itemMST.archived && value === false) {
        fieldsToUpdate.archived = false;
    }
    await updateItemDB(services, dsManager, workspaceId, itemMST.id, fieldsToUpdate)
}

async function setDocumentTrash(wsServerConnection: WsServerConnection, itemDB: ItemModelType, value: boolean) {
    await updateDocumentTrashed(
        wsServerConnection,
        {
            docId: itemDB.id!,
            trashed: value,
        });
}